<template>
	<div id="app">
		<component :is="currentBanner.banner" v-if="currentBanner" v-bind="currentBanner.props" />
		<ToastContainer />
		<RouterView :key="routeKey" />
		<transition name="transition--modal" mode="in-out">
			<component
				:is="currentModal.modal"
				v-if="currentModal"
				v-bind="currentModal.props"
				@modal-close="closeModal"
			/>
		</transition>
		<GdprCookieBanner v-if="!appStore.isMobileWebview" />
		<IntercomLauncher v-if="isIntercomEnabled"></IntercomLauncher>
	</div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { getScreenSize, isPreRender } from '@utils/client';
import { MaybeComputedRef, Script, useHead } from '@unhead/vue';
import { useDebounceFn, useEventListener, useIdle } from '@vueuse/core';
import { appCloudfrontPath } from '@constants';
import appConfig from './app.config.json';
import { CurrentBanner } from 'types/banner';
import { CurrentModal } from 'types/modal';
import GdprCookieBanner from '@components/layout/gdpr-cookie-banner.vue';
import IntercomLauncher from '@components/layout/intercom-launcher.vue';
import { stripRouteHash } from '@utils/routing';
import ToastContainer from '@components/layout/toast-container.vue';
import { useAppStore } from '@stores/app';
import { useRoute } from 'vue-router';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

// eslint-disable-next-line @typescript-eslint/no-empty-function
window.initMap = function () {
	// Intentionally left blank, callback is required. https://developers.google.com/maps/documentation/javascript/url-params#required_parameters
};

const route = useRoute();
const appStore = useAppStore();
const { idle } = useIdle(5 * 60 * 1000); // 5 min
const thirdPartyAuthStore = useThirdPartyAuthStore();

const baseScripts = [
	{
		src: `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_PLACES_API_KEY}&libraries=places&callback=initMap`,
		key: 'google-maps-script'
	},
	{
		src: `https://accounts.google.com/gsi/client`,
		key: 'google-sign-in-script',
		async: true,
		onload: () => (thirdPartyAuthStore.isGoogleLibraryLoaded = true)
	},
	{
		src: `https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js`,
		key: 'apple-sign-in-script',
		async: true,
		onload: () => (thirdPartyAuthStore.isAppleLibraryLoaded = true)
	},
	{ src: 'https://cdn.plaid.com/link/v2/stable/link-initialize.js', key: 'plaid-script' }
];

const metaDescription = `Invest in real estate, venture capital, and private credit with Fundrise—America's largest direct-access alternative asset manager.`;

const isIntercomEnabled = computed((): boolean => appStore.intercomEnabled);

const currentModal = computed((): CurrentModal | null => {
	document.body.style.overflow = appStore.currentModal ? 'hidden' : '';
	return appStore.currentModal;
});

const currentBanner = computed((): CurrentBanner | null => appStore.currentBanner);

const routeKey = computed((): string => stripRouteHash(route));
const defaultHeadControl = {
	titleTemplate: (title?: string) => (title ? `${title} | ${appConfig.title}` : appConfig.title),
	link: [
		{ rel: 'canonical', href: window.location.origin + window.location.pathname },
		{
			rel: 'icon',
			type: 'image/png',
			href: `${appCloudfrontPath}/images/global/favicon.png`
		}
	],
	meta: [
		{ charset: 'utf-8' },
		{ name: 'viewport', content: 'width=device-width, minimum-scale=1.0, maximum-scale=1.0' },
		{ name: 'apple-mobile-web-app-capable', content: 'yes' },
		{ name: 'p:domain_verify', content: '411182b397074a11ab6dd421b34bc4e8' },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: window.location.href },
		{ property: 'fb:app_id', content: appConfig.facebookAppId },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: '@fundrise' },
		{ name: 'description', content: metaDescription },
		{ property: 'og:description', content: metaDescription },
		{
			property: 'og:image',
			content: `${appCloudfrontPath + appConfig.metaImage}`
		}
	],
	script: baseScripts
};

const headControl = useHead(defaultHeadControl);

watch(idle, (idleValue) => {
	appStore.userIsIdle = idleValue;
});

async function isUserwayEnabled(): Promise<boolean> {
	const featureEnabled = await appStore.isFeatureEnabled('userway-accessibility-plugin');
	const isPreRenderUserAgent = isPreRender();
	const isDataViz = window.location.pathname.startsWith('/data-visualization') || route.name === 'data-visualization';
	return featureEnabled && !isPreRenderUserAgent && !isDataViz && !appStore.isMobileWebview;
}

async function initializeScripts(): Promise<void> {
	const scripts: MaybeComputedRef<Script<['script']>[]> = [...baseScripts];

	const userwayEnabled = await isUserwayEnabled();
	if (userwayEnabled) {
		scripts.push({
			src: 'https://cdn.userway.org/widget.js',
			key: 'userway-script',
			async: true,
			id: 'userway',
			'data-account': 'cVMkm5shQu'
		});
	}

	headControl?.patch({
		...defaultHeadControl,
		script: scripts
	});
}

function updateScreenSize(): void {
	appStore.updateScreenSize(getScreenSize());
}

function closeModal(): void {
	appStore.updateCurrentModal(null);
}

// Note - This is manually mounted. It will run before the route guards.
async function setup(): Promise<void> {
	updateScreenSize();
	useEventListener(window, 'load', useDebounceFn(updateScreenSize, 200));
	useEventListener(window, 'resize', useDebounceFn(updateScreenSize, 200));

	await appStore.initializeLaunchDarklyInstance();
	await initializeScripts();
}

setup();
</script>
